import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AdminLoginRequest, AuthResponse, ApiError, PermissionListResponse } from '../models/api.models';
import { PermissionsService } from './permissions.service';

// ==========================================
// AUTH SERVICE
// ==========================================

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly permissionsService = inject(PermissionsService);

    // Configuration
    private readonly API_BASE_URL = environment.apiBaseUrl;
    private readonly LOGIN_ENDPOINT = '/admin/login';
    private readonly LOGOUT_ENDPOINT = '/admin/logout';
    private readonly ME_PERMISSIONS_ENDPOINT = '/admin/me/permissions';
    private readonly TOKEN_KEY = 'admin_token';
    private readonly USER_KEY = 'current_user';

    // State management
    private readonly tokenSubject = new BehaviorSubject<string | null>(this.getStoredToken());
    public readonly token$ = this.tokenSubject.asObservable();

    private readonly currentUserSubject = new BehaviorSubject<any | null>(this.getStoredUser());
    public readonly currentUser$ = this.currentUserSubject.asObservable();

    /**
     * Login to admin panel
     */
    login(usuario: string, contrasena: string, remember: boolean = false): Observable<AuthResponse> {
        const url = `${this.API_BASE_URL}${this.LOGIN_ENDPOINT}`;
        const body: AdminLoginRequest = { usuario, contrasena };

        return this.http.post<AuthResponse>(url, body).pipe(
            tap(response => {
                this.storeToken(response.token, remember);
                this.tokenSubject.next(response.token);
            }),
            catchError(this.handleError)
        );
    }

    /**
     * Logout and clear stored token
     */
    logout(): Observable<void> {
        const url = `${this.API_BASE_URL}${this.LOGOUT_ENDPOINT}`;

        return this.http.post<void>(url, {}).pipe(
            tap(() => {
                this.clearToken();
                this.clearUser();
                this.tokenSubject.next(null);
                this.currentUserSubject.next(null);
                // Clear permissions cache
                this.permissionsService.clearCache();
            }),
            catchError((error) => {
                // Clear token even if logout request fails
                this.clearToken();
                this.clearUser();
                this.tokenSubject.next(null);
                this.currentUserSubject.next(null);
                // Clear permissions cache
                this.permissionsService.clearCache();
                return throwError(() => error);
            })
        );
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return !!this.getStoredToken();
    }

    /**
     * Get the current token
     */
    getToken(): string | null {
        return this.tokenSubject.value;
    }

    /**
     * Get the current user
     */
    getCurrentUser(): any | null {
        return this.currentUserSubject.value;
    }

    /**
     * Set the current user data
     */
    setCurrentUser(user: any | null): void {
        this.currentUserSubject.next(user);

        // Persist user to storage
        if (user) {
            this.storeUser(user);
        } else {
            this.clearUser();
        }
    }

    /**
     * Get current user's permissions from API
     * Should be called after successful login
     */
    getUserPermissions(): Observable<PermissionListResponse> {
        const url = `${this.API_BASE_URL}${this.ME_PERMISSIONS_ENDPOINT}`;
        return this.http.get<PermissionListResponse>(url).pipe(
            tap(response => {
                console.log('📥 Permissions received from API:', response.permisos);

                // Get current user or create a basic one
                let currentUser = this.currentUserSubject.value;

                if (!currentUser) {
                    // Create a basic user object with permissions
                    console.log('⚠️ No current user found, creating basic user object');
                    currentUser = {
                        id: 0, // Will be updated when we have user info
                        nombre: 'Usuario',
                        usuario: '',
                        permisos: response.permisos
                    };
                } else {
                    // Update existing user with permissions
                    console.log('✅ Updating existing user with permissions');
                    currentUser = {
                        ...currentUser,
                        permisos: response.permisos
                    };
                }

                // Set the updated user (this will also save to storage)
                this.setCurrentUser(currentUser);
                console.log('✅ User with permissions set in AuthService:', currentUser);
                console.log('User permissions loaded:', response.permisos.length, 'permissions');
            }),
            catchError((error) => {
                console.error('Error loading user permissions:', error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Store authentication token
     */
    private storeToken(token: string, remember: boolean): void {
        if (remember) {
            localStorage.setItem(this.TOKEN_KEY, token);
            sessionStorage.removeItem(this.TOKEN_KEY);
        } else {
            sessionStorage.setItem(this.TOKEN_KEY, token);
            localStorage.removeItem(this.TOKEN_KEY);
        }
    }

    /**
     * Get stored token from storage
     */
    private getStoredToken(): string | null {
        return localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
    }

    /**
     * Clear token from storage
     */
    private clearToken(): void {
        localStorage.removeItem(this.TOKEN_KEY);
        sessionStorage.removeItem(this.TOKEN_KEY);
    }

    /**
     * Store user data in storage
     */
    private storeUser(user: any): void {
        const userJson = JSON.stringify(user);

        // Store in the same location as the token for consistency
        if (localStorage.getItem(this.TOKEN_KEY)) {
            localStorage.setItem(this.USER_KEY, userJson);
            sessionStorage.removeItem(this.USER_KEY);
        } else if (sessionStorage.getItem(this.TOKEN_KEY)) {
            sessionStorage.setItem(this.USER_KEY, userJson);
            localStorage.removeItem(this.USER_KEY);
        } else {
            // Fallback: if no token, store in sessionStorage
            sessionStorage.setItem(this.USER_KEY, userJson);
        }
    }

    /**
     * Get stored user data from storage
     */
    private getStoredUser(): any | null {
        try {
            const userJson = localStorage.getItem(this.USER_KEY) || sessionStorage.getItem(this.USER_KEY);
            if (userJson) {
                return JSON.parse(userJson);
            }
        } catch (error) {
            console.error('Error parsing stored user data:', error);
            // Clear corrupted data
            localStorage.removeItem(this.USER_KEY);
            sessionStorage.removeItem(this.USER_KEY);
        }
        return null;
    }

    /**
     * Clear user data from storage
     */
    private clearUser(): void {
        localStorage.removeItem(this.USER_KEY);
        sessionStorage.removeItem(this.USER_KEY);
    }

    /**
     * Handle HTTP errors
     */
    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'Error de conexión. Verifique su conexión a internet e intente nuevamente.';

        if (error.error instanceof ErrorEvent) {
            // Client-side error
            if (environment.enableLogging) {
                console.error('Client error:', error.error.message);
            }
        } else {
            // Server-side error
            if (environment.enableLogging) {
                console.error(`Server error: ${error.status}`, error.error);
            }

            if (error.status === 401) {
                errorMessage = 'Usuario o contraseña incorrectos';
            } else if (error.status === 400) {
                errorMessage = error.error?.message || 'Datos de inicio de sesión inválidos';
            } else if (error.status === 0) {
                errorMessage = 'No se pudo conectar con el servidor';
            } else if (error.status === 500) {
                errorMessage = 'Error interno del servidor. Por favor, intente más tarde.';
            }
        }

        const apiError: ApiError = {
            message: errorMessage,
            details: error.error?.details || [],
            status: error.status
        };

        return throwError(() => apiError);
    }
}
